
import Mapbox from '../Mapbox';

/**
 * Wrapper seguro para document.startViewTransition que silencia AbortError.
 */
function safeStartTransition(updateDOM) {
    if (!document.startViewTransition) {
        updateDOM();
        return;
    }
    document.startViewTransition(updateDOM).finished.catch(err => {
        if (err.name !== 'AbortError') throw err;
    });
}

/**
 * Componente AlpineJS para búsqueda de edificios con Meilisearch
 * Integrado con la clase Mapbox mejorada
 *
 * @param {Array} initialCenter - Coordenadas iniciales [lng, lat]
 * @param {Number} initialZoom - Zoom inicial del mapa
 * @param {Object} options - Opciones adicionales de configuración
 * @param communeSlug - Slug de la comuna (deprecado, usar communeId)
 * @param communeId - ID de la comuna
 */
export default function buildingMapSearch(
    initialCenter = [-70.6483, -33.4569],
    initialZoom = 12,
    options = {},
    communeSlug = null,
    communeId = null
) {
    console.log('🏗️ Creando instancia de buildingMapSearch', { initialCenter, initialZoom, communeSlug, communeId });
    return {
        // Instancia del mapa
        mapbox: null,

        // Estado de los edificios
        buildings: [],
        totalBuildings: 0,
        selectedBuilding: null,
        highlightedBuildingId: null,

        // Estado de ordenamiento
        sortBy: 'relevance', // relevance, price-asc, price-desc, units-desc, name-asc
        sortOptions: [
            { value: 'relevance', label: 'Más relevantes' },
            { value: 'price-asc', label: 'Menor precio' },
            { value: 'price-desc', label: 'Mayor precio' },
        ],

        // Estado de carga
        loading: false,
        error: null,
        isMapReady: false,

        // Filtros
        filters: {
            min_price: null,
            max_price: null,
            bedrooms: [],
            bedrooms_exact_match: false,
            bathrooms: [],
            servicio_pro: false,
            multifamily: false,
            has_discount: false,
            free_commission: false,
            fixed_price: false,
            furnished: false,
            flex: false,
            accepts_pets: false,
            has_parking: false,
            has_storage: false,
            has_terrace: false,
            has_washer_connection: false,
            has_laundry: false,
            super_promo: false,
            close_to_metro: false,
            warranty_pro: false,
            no_require_aval: false,
            no_rent_warranty: false
        },

        // Filtros temporales (para el drawer)
        tempFilters: {
            min_price: null,
            max_price: null,
            bedrooms: [],
            bedrooms_exact_match: false,
            bathrooms: [],
            servicio_pro: false,
            multifamily: false,
            has_discount: false,
            free_commission: false,
            fixed_price: false,
            furnished: false,
            flex: false,
            accepts_pets: false,
            has_parking: false,
            has_storage: false,
            has_terrace: false,
            has_washer_connection: false,
            has_laundry: false,
            super_promo: false,
            close_to_metro: false,
            warranty_pro: false,
            no_require_aval: false,
            no_rent_warranty: false
        },

        // Configuración
        config: {
            limit: options.limit || 5000,
            autoSearch: options.autoSearch ?? true,
            debounceTime: options.debounceTime || 300,
            showDebugBbox: options.showDebugBbox || false,
            enableClustering: options.enableClustering ?? true,
            clusterRadius: options.clusterRadius || 50,
            clusterMaxZoom: options.clusterMaxZoom || 14,
            minZoom: options.minZoom || 12,
            maxZoom: options.maxZoom || 17,
            debug: options.debug || false,
        },

        // Referencias a handlers para cleanup
        eventHandlers: {},
        searchTimeout: null,
        abortController: null,
        currentSearchId: 0,

        // Estadísticas
        stats: {
            lastSearchTime: 0,
            totalSearches: 0,
            failedSearches: 0,
            averageResponseTime: 0,
        },

        // Flag para búsqueda por comuna
        communeSlug: communeSlug,
        communeId: communeId,

        // Nombre visual de la comuna (prioriza 'q', luego slug formateado, luego vacío)
        communeName: (new URLSearchParams(window.location.search)).get('q') ||
                     (communeSlug ? communeSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : ''),

        // Determina si es una búsqueda de comuna "oficial" (URL o Autocomplete)
        isCommuneSearch: !!communeSlug || !!communeId || (new URLSearchParams(window.location.search)).has('q'),

        // Coordenadas y zoom default para reset
        defaultCenter: {
            lng: initialCenter[0],
            lat: initialCenter[1],
        },
        defaultZoom: initialZoom,

        // Timestamp del último movimiento programático del mapa
        lastProgrammaticMapMove: 0,

        // Modo de vista (por defecto listado)
        viewMode: 'list',

        // Mostrar botón de búsqueda en el área
        showSearchButton: false,

        /**
         * Inicializar componente
         */
        init() {
            // Evitar múltiples inicializaciones
            if (this.mapbox) {
                this.log('⚠️ Mapa ya está inicializado, saltando init()');
                return;
            }

            this.log('🚀 Inicializando buildingMapSearch');

            // Asegurar que el nombre esté sincronizado con el input del buscador al cargar
            if (this.communeName) {
                window.dispatchEvent(new CustomEvent('update-autocomplete-search', {
                    detail: { search: this.communeName }
                }));
            }

            // Cargar filtros y ordenamiento desde URL
            const urlConfig = this.loadFromUrl();

            // Sincronizar filtros temporales con los cargados
            this.syncFiltersToTemp();

            const apiKey = import.meta.env.VITE_RADAR_PUBLISHABLE_KEY ||
                import.meta.env.VITE_MAPTILER_TOKEN;

            if (!apiKey) {
                console.error('❌ No se encontró VITE_RADAR_PUBLISHABLE_KEY o VITE_MAPTILER_TOKEN');
                this.error = 'Falta configuración de API key';
                return;
            }
            
            let center = initialCenter;
            let zoom = initialZoom;

            if (urlConfig && urlConfig.center) {
                center = urlConfig.center;
                zoom = urlConfig.zoom;
            } else if (Array.isArray(center) && center[1] < -60 && center[0] > -60) {
                 // Fallback healer for initialCenter if it is inverted
                 this.warn('🔄 InitialCenter invertido detectado, corrigiendo...');
                 center = [center[1], center[0]];
            }

            this.initializeMap(apiKey, center, zoom);
            this.setupEventListeners();

            this.log('✅ Componente inicializado');

            // Establecer un periodo de gracia inicial para evitar que movimientos automáticos de Mapbox limpien la comuna
            this.lastProgrammaticMapMove = Date.now();

            // Limpiar URL inmediatamente después de la inicialización si es necesario (evita flicker de lat/lng)
            this.updateUrl();
        },

        /**
         * Destruir el componente y limpiar recursos
         */
        destroy() {
            this.log('🧹 Destruyendo componente');

            // Cancelar búsqueda en progreso
            if (this.abortController) {
                this.abortController.abort();
            }

            // Limpiar timeout
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            // Remover event listeners
            this.removeEventListeners();

            // Destruir mapa y limpiar recursos
            if (this.mapbox) {
                this.mapbox.destroy();
                this.mapbox = null;
            }

            // Limpiar estado
            this.buildings = [];
            this.selectedBuilding = null;
            this.isMapReady = false;

            this.log('✅ Componente destruido');
        },
        /**
         * Inicializar mapa usando la clase Mapbox mejorada
         */
        initializeMap(apiKey, center, zoom) {
            // Verificar si ya existe un mapa en el contenedor
            const mapContainer = document.getElementById('map');
            if (!mapContainer) {
                this.warn('❌ Contenedor #map no encontrado');
                return;
            }

            // Si el contenedor ya tiene un mapa, no crear uno nuevo
            if (mapContainer.classList.contains('maplibregl-map') || mapContainer.querySelector('.maplibregl-canvas')) {
                this.warn('⚠️ Ya existe un mapa en el contenedor, saltando inicialización');
                return;
            }

            this.log('🗺️ Inicializando mapa', { center, zoom });

            // Crear instancia con configuración mejorada
            this.mapbox = new Mapbox(apiKey, {
                debug: this.config.debug,
                primaryColor: options.primaryColor || '#3B82F6',
                markerColor: options.markerColor || '#3B82F6',
                textColor: options.textColor || '#3B82F6',
            });

            // Configurar mapa
            this.mapbox.makeMap('map', {
                center: center,
                zoom: zoom,
                minZoom: this.config.minZoom,
                maxZoom: this.config.maxZoom,
            }, {
                showCompass: false,
                showZoom: true,
            });

            this.log('✅ Mapa inicializado');
        },

        /**
         * Configurar listeners de eventos del mapa
         */
        setupEventListeners() {
            this.log('📡 Configurando event listeners');

            // Mapa cargado
            this.eventHandlers.mapLoad = ((e) => {
                this.log('📍 Mapa cargado', e.detail);
                this.isMapReady = true;

                // Fix: Asegurar que el mapa dimensiona correctamente si inicia en un contenedor "pequeño" o oculto
                if (this.mapbox && this.mapbox.map) {
                    this.mapbox.map.resize();
                }

                // Pequeño delay para asegurar que el resize surta efecto antes de calcular bounds
                setTimeout(() => {
                    // Reforzar resize justo antes de buscar
                    if (this.mapbox && this.mapbox.map) this.mapbox.map.resize();

                    if (this.communeId || this.communeSlug) {
                        const center = this.mapbox.map.getCenter();
                        const zoom = this.mapbox.map.getZoom();
                        this.performCommuneSearch({ lat: center.lat, lng: center.lng, zoom: zoom });
                    } else {
                        this.performInitialSearch();
                    }
                }, 800);
            }).bind(this);

            // Mapa movido
            this.eventHandlers.mapMoveEnd = ((e) => {
                const isProgrammatic = !!e.detail.programmatic;
                const source = e.detail.source;
                const forceSearch = !!e.detail.forceSearch;
                
                if (this.isMapReady) {
                    this.log('📍 mapMoveEnd handler', { isProgrammatic, source, forceSearch });

                    // Mostrar botón de búsqueda manual solo si el movimiento fue REALMENTE manual
                    // y ha pasado el tiempo de gracia inicial
                    const now = Date.now();
                    const isManual = !isProgrammatic && (!this.lastProgrammaticMapMove || (now - this.lastProgrammaticMapMove) > 3500);
                    if (isManual) {
                        this.showSearchButton = true;
                    }

                    this.updateUrl(true, isProgrammatic);
                }

                // Si es una búsqueda forzada por autocomplete o flag específico, ignorar el bloqueo programático
                if (source === 'autocomplete' || forceSearch) {
                    this.log('🚀 Ejecutando búsqueda forzada (origen controlado)');
                    this.searchBuildings(source || 'forced_map_move');
                    return;
                }

                // SIEMPRE saltar búsqueda si es un movimiento programático (flyTo, fitBounds) estándar
                if (isProgrammatic) {
                    this.log('⏭️ Saltando búsqueda automática: movimiento programático detectado');
                    return;
                }

                // Si hubo una operación programática manual reciente (threshold de seguridad), saltar búsqueda
                const now = Date.now();
                const GLOBAL_THRESHOLD = 5000;
                if (this.lastProgrammaticMapMove && (now - this.lastProgrammaticMapMove) < GLOBAL_THRESHOLD) {
                    this.log('⏭️ Saltando búsqueda automática: dentro de ventana de seguridad');
                    return;
                }

                if (this.config.autoSearch) {
                    this.debouncedSearch();
                }
            }).bind(this);

            // Click en cluster
            this.eventHandlers.clusterClick = ((e) => {
                this.log('👥 Cluster clickeado', e.detail);
                this.handleClusterClick(e.detail.ids);
            }).bind(this);

            // Click en edificio individual
            this.eventHandlers.unclusteredClick = ((e) => {
                this.log('🏢 Edificio clickeado', e.detail);
                this.handleBuildingClick(e.detail.ids[0]);
            }).bind(this);

            // Agregar listeners
            window.addEventListener('mapbox-load', this.eventHandlers.mapLoad, { once: true });
            window.addEventListener('mapbox-moveend', this.eventHandlers.mapMoveEnd);
            window.addEventListener('cluster', this.eventHandlers.clusterClick);
            window.addEventListener('unclustered', this.eventHandlers.unclusteredClick);
            
            // Listener para autocomplete
            this.eventHandlers.autocomplete = ((e) => this.handleAutocomplete(e)).bind(this);
            window.addEventListener('autocomplete-selected', this.eventHandlers.autocomplete);

            // Listener para submit del autocomplete (cuando se presiona Enter o Buscar)
            this.eventHandlers.autocompleteSubmit = ((e) => {
                e.preventDefault();
                this.handleAutocompleteSubmit(e);
            }).bind(this);
            window.addEventListener('autocomplete-submit', this.eventHandlers.autocompleteSubmit);

            this.log('✅ Event listeners configurados');
        },

        /**
         * Manejar evento de autocomplete
         */
        handleAutocomplete(e) {
            const detail = e.detail;
            this.log('📍 Autocomplete selection:', detail);

            if (detail.lat && detail.lng) {
                 // Google Place / Dirección
                 this.log('🗺️ Moviendo a ubicación de Google:', detail);
                 this.lastProgrammaticMapMove = Date.now();
                 
                 this.mapbox.map.flyTo({
                     center: [detail.lng, detail.lat],
                     zoom: 15,
                     essential: true
                 });

                 this.isCommuneSearch = false;
                 this.communeName = '';

                 // Forzar búsqueda posterior
                 // 'autocomplete_submit' está en la whitelist y saltará el bloqueo de movimiento programático
                 setTimeout(() => {
                     this.searchBuildings('autocomplete_submit');
                 }, 1200);

            } else if (detail.slug || detail.communeId) {
                // Comuna
                this.communeSlug = detail.slug || null;
                this.communeId = detail.communeId || null;
                this.isCommuneSearch = true;

                // Actualizar nombre visual inmediatamente
                if (detail.name) {
                    this.communeName = detail.name;
                }

                this.performCommuneSearch(detail.slug || detail.communeId);
            }
        },

        /**
         * Manejar evento de submit del autocomplete
         */
        handleAutocompleteSubmit(e) {
            const detail = e.detail;
            this.log('🚀 Autocomplete submit:', detail);

            // Aplicar filtros de dormitorios
            if (detail.bedrooms && detail.bedrooms.length > 0) {
                this.filters.bedrooms = detail.bedrooms;
                this.filters.bedrooms_exact_match = detail.exactMatch || false;
            }

            // Preservar type si viene del autocomplete (solo para sales)
            if (detail.type) {
                this.filters.type = detail.type;
            }

            if (detail.slug || detail.communeId) {
                // Búsqueda por comuna
                this.communeSlug = detail.slug || null;
                this.communeId = detail.communeId || null;
                this.isCommuneSearch = true;
                this.communeName = detail.search || '';

                if (detail.lat && detail.lng) {
                    this.performCommuneSearch({ lat: detail.lat, lng: detail.lng, zoom: detail.zoom });
                } else {
                    this.performCommuneSearch(detail.slug || detail.communeId);
                }
            } else if (detail.lat && detail.lng) {
                // Dirección puntual
                this.isCommuneSearch = false;
                this.communeName = '';

                const zoom = parseFloat(detail.zoom) || 13;
                this.lastProgrammaticMapMove = Date.now();

                // Actualizar URL inmediatamente
                this.updateUrl(false, false, { lat: detail.lat, lng: detail.lng }, zoom);

                if (this.mapbox && this.mapbox.map) {
                    this.mapbox.map.flyTo({
                        center: [detail.lng, detail.lat],
                        zoom: zoom,
                        duration: 1500
                    }, { programmatic: true, source: 'autocomplete' });

                    setTimeout(() => {
                        this.searchBuildings('autocomplete_submit');
                    }, 1600);
                }
            } else {
                // Solo filtros, buscar en área actual
                if (this.searchBuildings) {
                    this.searchBuildings();
                }
            }
        },

        /**
         * Remover todos los event listeners
         */
        removeEventListeners() {
            if (this.eventHandlers.mapLoad) {
                window.removeEventListener('mapbox-load', this.eventHandlers.mapLoad);
            }
            if (this.eventHandlers.mapMoveEnd) {
                window.removeEventListener('mapbox-moveend', this.eventHandlers.mapMoveEnd);
            }
            if (this.eventHandlers.clusterClick) {
                window.removeEventListener('cluster', this.eventHandlers.clusterClick);
            }
            if (this.eventHandlers.unclusteredClick) {
                window.removeEventListener('unclustered', this.eventHandlers.unclusteredClick);
            }
            if (this.eventHandlers.autocomplete) {
                window.removeEventListener('autocomplete-selected', this.eventHandlers.autocomplete);
            }
            if (this.eventHandlers.autocompleteSubmit) {
                window.removeEventListener('autocomplete-submit', this.eventHandlers.autocompleteSubmit);
            }

            this.eventHandlers = {};
        },
        /**
         * Búsqueda inicial (sin debounce)
         */
        performInitialSearch() {
            this.log('🔍 Realizando búsqueda inicial');
            this.searchBuildings('initial_load');
        },

        /**
         * Búsqueda inicial por comuna cuando no hay coordenadas en URL
         */
        async performCommuneSearch(fallback = null) {
            // DIAGNÓSTICO: Rastrear quién llama a esta función
            if (this.config.debug) {
                console.group(`🏘️ performCommuneSearch Trace`);
                console.trace();
                console.groupEnd();
            }

            // Normalizar el objeto fallback si existe para evitar coordenadas "random" (invertidas o mal formadas)
            if (fallback) {
                let lat = parseFloat(fallback.lat);
                let lng = parseFloat(fallback.lng);
                let zoom = parseFloat(fallback.zoom);
                
                if (!isNaN(lat) && !isNaN(lng)) {
                    // Healer: Detectar coordenadas invertidas típicas de Chile
                    // Latitud Chile: ~ -17 a -56 | Longitud Chile: ~ -66 a -76
                    if (lat < -60 && lng > -60) {
                        this.warn('🔄 Detectadas coordenadas fallback invertidas, corrigiendo...', { lat, lng });
                        const actualLat = lng;
                        const actualLng = lat;
                        lat = actualLat;
                        lng = actualLng;
                    }
                    
                    fallback.lat = lat;
                    fallback.lng = lng;
                    // Validar zoom: si es inválido o extremo, usar default
                    fallback.zoom = (!isNaN(zoom) && zoom > 0 && zoom < 22) ? zoom : 13;
                    
                    this.log('🛡️ Fallback validado y saneado:', { lat: fallback.lat, lng: fallback.lng, zoom: fallback.zoom });
                } else {
                    this.warn('⚠️ Fallback con coordenadas no numéricas detectado, ignorando fallback', fallback);
                    fallback = null;
                }
            }

            if (!this.communeId && !this.communeSlug) {
                this.warn('⏭️ Saltando performCommuneSearch: communeId y communeSlug no definidos');
                return;
            }

            if (!this.communeId) {
                this.error = 'No se pudo obtener el ID de la comuna. Por favor, intenta buscar de nuevo.';
                this.warn('❌ communeId no definido. Búsqueda por comuna cancelada.', {
                    communeSlug: this.communeSlug,
                    communeName: this.communeName
                });

                // Si tenemos coordenadas de fallback, intentar buscar por ellas
                if (fallback?.lat && fallback?.lng) {
                    this.log('📍 Intentando búsqueda por coordenadas de fallback');
                    const zoom = fallback.zoom || 13;
                    this.lastProgrammaticMapMove = Date.now();
                    this.mapbox.map.flyTo({
                        center: [fallback.lng, fallback.lat],
                        zoom: zoom,
                        duration: 2000
                    }, { programmatic: true });
                }
                return;
            }

            if (this.loading) {
                this.log('🛑 Saltando performCommuneSearch: ya hay una operación en curso');
                return;
            }

            this.log('🏘️ Iniciando búsqueda por comuna', { commune_slug: this.communeSlug });

            if (!this.mapbox?.map) {
                this.warn('⚠️ Mapa no disponible para búsqueda por comuna');
                return;
            }

            // Incrementar ID para identificar esta operación
            this.currentSearchId++;
            const searchId = this.currentSearchId;

            try {
                this.loading = true;
                this.error = null;

                // Validar que communeId sea un número válido
                const communeIdInt = parseInt(this.communeId);
                if (isNaN(communeIdInt) || communeIdInt <= 0) {
                    throw new Error('commune_id inválido: ' + this.communeId);
                }

                // Usar el nuevo endpoint de búsqueda por comuna (sin bounding box)
                const payload = {
                    commune_id: communeIdInt,
                    filters: this.getActiveFilters(),
                    sort: this.sortBy,
                    limit: this.config.limit,
                };

                if (this.config.debug) {
                    this.log('📤 Enviando búsqueda por comuna:', payload);
                }

                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

                const response = await fetch('/api/buildings/search-by-commune', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken || '',
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Si mientras esperábamos el fetch se inició otra búsqueda, abortar procesamiento
                if (searchId !== this.currentSearchId) {
                    this.log(`⚠️ Búsqueda por comuna #${searchId} quedó obsoleta`);
                    return;
                }

                const data = await response.json();

                // Manejar el formato de respuesta: data.data.buildings
                const buildings = data.data?.buildings || data.buildings || [];
                const total = data.data?.total || data.total || 0;

                if (buildings.length > 0) {
                    this.buildings = buildings;
                    this.totalResults = total;

                    // Marcar timestamp antes de mover el mapa para evitar búsqueda duplicada por moveend
                    this.lastProgrammaticMapMove = Date.now();

                    if (this.config.debug) {
                        const first = buildings[0];
                        this.log('🏗️ Edificios encontrados:', { 
                            count: buildings.length, 
                            firstGeo: first._geo, 
                            communeId: this.communeId,
                            communeSlug: this.communeSlug
                        });
                    }

                    // Centrar el mapa en los edificios encontrados
                    this.fitMapToBuildingBounds(buildings);

                    // Actualizar marcadores en el mapa
                    await this.updateMapMarkers();

                    this.log('✅ Búsqueda por comuna completada', {
                        totalBuildings: buildings.length,
                        communeId: this.communeId,
                        communeSlug: this.communeSlug
                    });
                } else {
                    this.log('⚠️ Búsqueda finalizada: No se encontraron edificios para la comuna', {
                        communeId: this.communeId,
                        communeSlug: this.communeSlug
                    });

                    // Si no hay resultados, limpiar marcadores y mover el mapa a la ubicación (fallback) para mostrar el área vacía
                    this.buildings = [];
                    this.totalResults = 0;
                    await this.updateMapMarkers();

                    if (fallback && fallback.lat && fallback.lng) {
                        this.log('📍 Moviendo a ubicación de la comuna (sin resultados)');
                        const zoom = fallback.zoom || 13;
                        this.lastProgrammaticMapMove = Date.now();
                        
                        // Actualizar URL con el centro de la comuna
                        this.updateUrl(false, false, { lat: fallback.lat, lng: fallback.lng }, zoom);
                        
                        this.mapbox.map.flyTo({
                            center: [fallback.lng, fallback.lat],
                            zoom: zoom,
                            duration: 2000
                        }, { programmatic: true });
                    }
                    
                    // NO limpiar communeSlug ni realizar búsqueda inicial,
                    // manteniéndonos en el estado de "búsqueda en X comuna (0 resultados)"
                }

            } catch (error) {
                if (searchId === this.currentSearchId) {
                    console.error('❌ Error en búsqueda por comuna:', error);
                    this.performInitialSearch();
                }
            } finally {
                if (searchId === this.currentSearchId) {
                    this.loading = false;
                }
            }
        },

        /**
         * Ajustar el mapa para que muestre todos los edificios
         */
        fitMapToBuildingBounds(buildings, options = {}) {
            if (!this.mapbox || !this.mapbox.map || buildings.length === 0) {
                return;
            }

            this.lastProgrammaticMapMove = Date.now();
            
            const duration = options.animate === false ? 0 : (options.duration || 2000);

            // Filtrar solo coordenadas válidas dentro o cerca de Chile para evitar saltos locos (ej: lat 60 o el pacífico)
            // Filtrar solo coordenadas válidas
            const allCoordinates = buildings
                .filter(b => b._geo && b._geo.lat && b._geo.lng)
                .map(b => ({
                    lat: parseFloat(b._geo.lat),
                    lng: parseFloat(b._geo.lng)
                }));
            
            if (allCoordinates.length === 0) {
                 this.log('⚠️ No valid coordinates found for bounds');
                 return;
            }

            // 1. Calcular Mediana para Lat y Lng (Robusto ante outliers)
            const sortedLats = [...allCoordinates].sort((a, b) => a.lat - b.lat);
            const sortedLngs = [...allCoordinates].sort((a, b) => a.lng - b.lng);
            const mid = Math.floor(allCoordinates.length / 2);
            const medianLat = sortedLats[mid].lat;
            const medianLng = sortedLngs[mid].lng;

            if (this.config.debug) {
                this.log('📊 Stats de Coordenadas:', {
                    count: allCoordinates.length,
                    medianLat,
                    medianLng,
                    minLat: sortedLats[0].lat,
                    maxLat: sortedLats[sortedLats.length - 1].lat
                });
            }

            // 2. Filtrar Outliers: Mantener solo los que están cerca de la mediana
            // Threshold de 0.5 grados es aprox 55km (cubre todo Gran Santiago y más)
            const OUTLIER_THRESHOLD = 0.5; 
            
            const coordinates = allCoordinates
                .filter(c => 
                       Math.abs(c.lat - medianLat) < OUTLIER_THRESHOLD &&
                       Math.abs(c.lng - medianLng) < OUTLIER_THRESHOLD
                )
                .map(c => [c.lng, c.lat]);

            if (coordinates.length === 0) {
                this.warn('⚠️ Todos los puntos fueron filtrados como outliers? Usando mediana.', { medianLat, medianLng });
                // Fallback a la mediana si todo falla
                this.mapbox.map.flyTo({ center: [medianLng, medianLat], zoom: 13, duration: duration }, { programmatic: true });
                return;
            }

            // Si se filtraron puntos, avisar en debug
            if (coordinates.length < allCoordinates.length && this.config.debug) {
                 this.warn(`🧹 Filtrados ${allCoordinates.length - coordinates.length} outliers lejanos al cluster principal.`);
            }

            // Si solo hay un edificio, centrar en él
            if (coordinates.length === 1) {
                const targetCenter = coordinates[0];
                const targetZoom = 14;
                
                // Actualizar URL inmediatamente con el destino final
                this.updateUrl(false, false, { lat: targetCenter[1], lng: targetCenter[0] }, targetZoom);

                this.mapbox.map.flyTo({
                    center: targetCenter,
                    zoom: targetZoom,
                    duration: duration
                }, { programmatic: true });
                
                this.log('🗺️ Mapa centrado en un edificio');
                return;
            }

            // Calcular bounds
            let minLng = coordinates[0][0];
            let maxLng = coordinates[0][0];
            let minLat = coordinates[0][1];
            let maxLat = coordinates[0][1];

            coordinates.forEach(([lng, lat]) => {
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
            });

            const bounds = [[minLng, minLat], [maxLng, maxLat]];

            // Predecir la cámara final para actualizar la URL inmediatamente
            const cameraOptions = {
                padding: { top: 50, bottom: 50, left: 50, right: 50 },
                maxZoom: 14
            };
            
            const predictedCamera = this.mapbox.map.cameraForBounds(bounds, cameraOptions);
            if (predictedCamera) {
                this.updateUrl(false, false, predictedCamera.center, predictedCamera.zoom);
            }

            // Ajustar el mapa
            this.mapbox.map.fitBounds(bounds, {
                ...cameraOptions,
                duration: duration
            }, { programmatic: true });

            this.log('🗺️ Mapa ajustado a los límites de los edificios', {
                buildingsCount: coordinates.length,
                bounds
            });
        },

        /**
         * Búsqueda con debounce
         */
        debouncedSearch() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.log('⏱️ Debouncing search...');
            this.searchTimeout = setTimeout(() => {
                // Verificar una última vez si estamos en ventana programática antes de disparar
                const now = Date.now();
                const GLOBAL_THRESHOLD = 5000;
                if (this.lastProgrammaticMapMove && (now - this.lastProgrammaticMapMove) < GLOBAL_THRESHOLD) {
                    this.log('⏭️ Debounce cancelado: ventana programática detectada al final del timeout');
                    return;
                }
                this.searchBuildings('debounced_auto_search');
            }, this.config.debounceTime);
        },

        /**
         * Calcular bounds sintéticos basados en centro y zoom
         */
        calculateSyntheticBounds(center, zoom) {
             const lat = center.lat;
             const lng = center.lng;
             const deltaMsg = 180 / Math.pow(2, zoom); 
             // Usar un factor mayor para asegurar que cubrimos suficiente área (fallback seguro)
             const factor = 3.0; 
             const deltaLat = deltaMsg * factor; 
             const deltaLng = deltaMsg * factor;
             const sw = [lng - deltaLng, lat - deltaLat];
             const ne = [lng + deltaLng, lat + deltaLat];
             return [sw, ne];
        },

        /**
         * Buscar edificios usando Meilisearch
         */
        async searchBuildings(reason = 'unknown') {
            // DIAGNÓSTICO
            if (this.config.debug) {
                // console.group(`🔍 searchBuildings Trace: ${reason}`);
                // console.trace();
                // console.groupEnd();
            }

            if (!this.mapbox?.map) {
                this.warn(`⚠️ Mapa no está listo para búsqueda (${reason})`);
                return;
            }

            // Al buscar manualmente o por autocomplete, ocultar el botón
            if (reason === 'manual_search_area' || reason === 'autocomplete_submit' || reason === 'initial_load') {
                this.showSearchButton = false;
            }

            // Obtener bounds con fallback para mapas colapsados (ej: carga inicial sin resize)
            let bounds = this.mapbox.map.getBounds();
            const canvasHeight = this.mapbox.map.getCanvas().clientHeight;
            
            // Check para bounds colapsados (punto único) o canvasHeight 0
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            const isCollapsed = (Math.abs(sw.lat - ne.lat) < 0.00001 && Math.abs(sw.lng - ne.lng) < 0.00001);
            
            if (reason === 'initial_load' || canvasHeight < 100 || isCollapsed) {
                this.warn(`⚠️ Detectado mapa inestable o carga inicial (${reason}, h=${canvasHeight}). Usando bounds sintéticos.`);
                const center = this.mapbox.map.getCenter();
                const zoom = this.mapbox.map.getZoom();
                // Usar bounds sintéticos directamente (array format) para evitar dependencia de mapboxgl global
                bounds = this.calculateSyntheticBounds(center, zoom);
            }

            // Seguridad extra: si acabamos de hacer un movimiento programático...
            const now = Date.now();
            const MOVEMENT_THRESHOLD = 5000; 
            if (reason !== 'commune_search' && reason !== 'initial_load' && reason !== 'autocomplete_submit' && reason !== 'manual_search_area' && 
                this.lastProgrammaticMapMove && (now - this.lastProgrammaticMapMove) < MOVEMENT_THRESHOLD) {
                this.log(`⏭️ Ignorando searchBuildings (${reason}): ventana programática activa`);
                return;
            }

            // Cancelar debounce
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
                this.searchTimeout = null;
            }

            // Evitar peticiones concurrentes idénticas
            if (this.loading && this.abortController && reason === 'debounced_auto_search') {
                this.log('⏳ Ya hay una búsqueda automática en curso, cancelando la anterior...');
            }

            // Cancelar búsqueda anterior si existe
            if (this.abortController) {
                this.abortController.abort();
            }

            // Incrementar ID de búsqueda para identificar esta búsqueda específica
            this.currentSearchId++;
            const searchId = this.currentSearchId;

            this.abortController = new AbortController();
            this.loading = true;
            this.error = null;

            const startTime = performance.now();

            try {
                let data;
                // Umbral de zoom para activar chunking (Zoom < 13.5 significa ver mucha área "País/Región")
                const CHUNK_ZOOM_THRESHOLD = 13.5; 
                const currentZoom = this.mapbox.map.getZoom();

                // Normalizar bounds a [west, south, east, north] inmediatamente para uso consistente
                let boundsArray;
                if (bounds.toArray) { // LngLatBounds
                     const arr = bounds.toArray(); // [[w,s],[e,n]]
                     boundsArray = [arr[0][0], arr[0][1], arr[1][0], arr[1][1]];
                } else if (Array.isArray(bounds) && bounds.length === 4 && typeof bounds[0] === 'number') {
                     boundsArray = bounds;
                } else if (Array.isArray(bounds) && bounds.length === 2 && Array.isArray(bounds[0])) {
                     // [[w,s],[e,n]] format (synthetic)
                     boundsArray = [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]];
                }

                // Actualizar bbox interno para consistencia
                this.mapbox.bbox = boundsArray;

                // Usar estrategia de chunks si estamos "lejos" (zoom bajo) y es una búsqueda estándar
                if (currentZoom < CHUNK_ZOOM_THRESHOLD && reason !== 'commune_search') {
                     this.log(`🧩 Ejecutando búsqueda paralela (Chunks 2x2) - Zoom: ${currentZoom.toFixed(1)}`);
                     data = await this.fetchBuildingsChunked(boundsArray);
                } else {
                    const payload = this.buildSearchPayload(boundsArray);
                    this.log(`🔍 Buscando edificios (${reason}) #${searchId}`, payload);

                    // Dibujar bbox para debugging si está habilitado
                    if (this.config.showDebugBbox) {
                        this.drawBoundingBox(payload.bounds);
                    }

                    const response = await this.fetchBuildings(payload);

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                    }

                    data = await response.json();
                }
                await this.processSearchResults(data);
    


                // Actualizar estadísticas
                const endTime = performance.now();
                const searchTime = endTime - startTime;
                this.updateSearchStats(searchTime, true);

                this.log(`✅ Búsqueda #${searchId} completada en ${searchTime.toFixed(2)}ms. Edificios: ${this.buildings.length}`);

            } catch (error) {
                if (error.name === 'AbortError') {
                    this.log('⚠️ Búsqueda cancelada');
                    return;
                }

                console.error('❌ Error al buscar edificios:', error);
                // Solo establecer error si es la búsqueda más reciente
                if (searchId === this.currentSearchId) {
                    this.error = error.message;
                }
                this.updateSearchStats(0, false);

                // NO limpiar edificios existentes - mantener últimos resultados válidos
            } finally {
                // Solo establecer loading = false si esta es la búsqueda más reciente
                // Esto evita que búsquedas canceladas apaguen el loading de búsquedas nuevas
                if (searchId === this.currentSearchId) {
                    this.log('🔚 Finalizando búsqueda #' + searchId + ', estableciendo loading = false');
                    this.loading = false;
                    this.abortController = null;
                } else {
                    this.log('⚠️ Búsqueda #' + searchId + ' obsoleta, no modificando loading (actual: #' + this.currentSearchId + ')');
                }
            }
        },

        /**
         * Construir payload de búsqueda
         */
        buildSearchPayload(bounds) {
            // El bbox de Mapbox es: [west, south, east, north]
            return {
                bounds: {
                    north: bounds[3],
                    south: bounds[1],
                    east: bounds[2],
                    west: bounds[0]
                },
                filters: this.getActiveFilters(),
                limit: this.config.limit,
                sort: this.sortBy
            };
        },

        /**
         * Realizar petición fetch a la API
         */
        async fetchBuildings(payload) {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

            return fetch('/api/buildings/search-by-bbox', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken || '',
                    'Accept': 'application/json',
                },
                body: JSON.stringify(payload),
                signal: this.abortController.signal,
            });
        },

        /**
         * Realizar búsqueda dividiendo el bbox en cuadrantes para paralelizar
         */
        async fetchBuildingsChunked(overrideBounds = null) {
            let bounds;

            if (overrideBounds) {
                 // Normalizar overrideBounds
                if (overrideBounds.toArray) { // LngLatBounds
                     const arr = overrideBounds.toArray();
                     bounds = [arr[0][0], arr[0][1], arr[1][0], arr[1][1]];
                } else if (Array.isArray(overrideBounds) && overrideBounds.length === 2 && Array.isArray(overrideBounds[0])) {
                     // [[w,s],[e,n]]
                     bounds = [overrideBounds[0][0], overrideBounds[0][1], overrideBounds[1][0], overrideBounds[1][1]];
                } else {
                    bounds = overrideBounds; // Assume [w,s,e,n]
                }
            } else {
                 bounds = this.mapbox.bbox;
            }

            if (!bounds) return { success: false, data: { buildings: [], total: 0 } };

            const [w, s, e, n] = bounds;
            const currentZoom = this.mapbox?.map?.getZoom() || 12;
            let GRID_SIZE = 2;

            if (currentZoom < 8) {
                GRID_SIZE = 4; // 16 chunks for very low zooms (country level)
            } else if (currentZoom < 11) {
                GRID_SIZE = 3; // 9 chunks for regional zooms
            }
            
            const latStep = (n - s) / GRID_SIZE;
            const lngStep = (e - w) / GRID_SIZE;
            
            const chunks = [];

            for (let row = 0; row < GRID_SIZE; row++) {
                for (let col = 0; col < GRID_SIZE; col++) {
                    const chunkWest = w + (lngStep * col);
                    const chunkSouth = s + (latStep * row);
                    const chunkEast = chunkWest + lngStep;
                    const chunkNorth = chunkSouth + latStep;
                    
                    chunks.push([chunkWest, chunkSouth, chunkEast, chunkNorth]);
                }
            }

            // Ejecutar peticiones en paralelo
            this.log(`🧩 Iniciando búsqueda chunked en grid ${GRID_SIZE}x${GRID_SIZE}. BBox world:`, bounds);

            const promises = chunks.map((chunkBounds, index) => {
                const payload = this.buildSearchPayload(chunkBounds);
                return this.fetchBuildings(payload)
                    .then(async res => {
                        if (res.ok) {
                            const json = await res.json();
                            return json;
                        } else {
                            this.warn(`  🔸 Chunk ${index} HTTP Error: ${res.status}`);
                            return { success: false, data: { buildings: [], total: 0 } };
                        }
                    })
                    .catch(err => {
                        if (err.name !== 'AbortError') this.warn(`  ❌ Chunk ${index} Failed:`, err);
                        return { success: false, data: { buildings: [] } };
                    });
            });

            const results = await Promise.all(promises);

            // Agregar y deduplicar resultados
            // IMPORTANTE: Deduplicar por 'id' (único por documento/unidad) y NO por 'building_id'
            // para evitar que se colapsen las unidades retail en la lista de resultados.
            const buildingsMap = new Map();
            
            results.forEach(res => {
                if (res.success && res.data?.buildings) {
                    res.data.buildings.forEach(b => {
                        const id = b.id || b.unit_id || b.building_id;
                        if (!buildingsMap.size || !buildingsMap.has(id)) {
                            buildingsMap.set(id, b);
                        }
                    });
                }
            });

            this.log(`🧩 Chunks combinados: ${buildingsMap.size} documentos únicos encontrados.`);

            const uniqueBuildings = Array.from(buildingsMap.values());
            const sortedBuildings = this._sortBuildingsList(uniqueBuildings);
            
            return {
                success: true,
                data: {
                    buildings: sortedBuildings,
                    total: buildingsMap.size
                }
            };
        },
        /**
         * Procesar resultados de búsqueda
         */
        async processSearchResults(data) {
            // this.log('📥 processing data:', data); 
            if (!data.success) {
                throw new Error(data.message || 'Error en la búsqueda');
            }

            const newBuildings = data.data.buildings || [];
            const total = data.data.total || 0;

            this.log(`📦 Recibidos ${newBuildings.length} de ${total} edificios`);

            // Validar y filtrar edificios con coordenadas válidas
            const validBuildings = this.validateBuildings(newBuildings);

            // Actualizar mapa usando los datos crudos (mucho más rápido que iterar el proxy de Alpine)
            await this.updateMapMarkers(validBuildings);

            this.buildings = validBuildings;
            this.totalBuildings = total;
        },

        /**
         * Validar edificios y filtrar los que tienen coordenas válidas
         */
        validateBuildings(buildings) {
            const valid = buildings.filter(b => {
                const hasValidGeo = b._geo &&
                    typeof b._geo.lat === 'number' &&
                    typeof b._geo.lng === 'number' &&
                    !isNaN(b._geo.lat) &&
                    !isNaN(b._geo.lng) &&
                    b._geo.lat >= -90 &&
                    b._geo.lat <= 90 &&
                    b._geo.lng >= -180 &&
                    b._geo.lng <= 180;

                if (!hasValidGeo) {
                    this.warn(`⚠️ Edificio ${b.building_id} sin coordenadas válidas:`, b._geo);
                }

                return hasValidGeo;
            });

            this.log(`✅ ${valid.length}/${buildings.length} edificios con coordenadas válidas`);
            return valid;
        },

        /**
         * Actualizar estadísticas de búsqueda
         */
        updateSearchStats(searchTime, success) {
            this.stats.totalSearches++;

            if (!success) {
                this.stats.failedSearches++;
            }

            if (searchTime > 0) {
                this.stats.lastSearchTime = searchTime;

                // Calcular promedio móvil
                const alpha = 0.3; // Factor de suavizado
                this.stats.averageResponseTime =
                    alpha * searchTime + (1 - alpha) * this.stats.averageResponseTime;
            }
        },

        /**
         * Obtener solo los filtros activos
         */
        getActiveFilters() {
            const filters = {};

            // Filtros numéricos
            if (this.filters.min_price) filters.min_price = Number(this.filters.min_price);
            if (this.filters.max_price) filters.max_price = Number(this.filters.max_price);

            // Arrays - convertir a arrays normales para evitar Proxies
            if (this.filters.bedrooms.length > 0) {
                filters.bedrooms = Array.isArray(this.filters.bedrooms)
                    ? [...this.filters.bedrooms]
                    : this.filters.bedrooms;
            }
            // Solo enviar bedrooms_exact_match si hay bedrooms seleccionados
            if (this.filters.bedrooms.length > 0 && this.filters.bedrooms_exact_match) {
                filters.bedrooms_exact_match = true;
            }

            if (this.filters.bathrooms.length > 0) {
                filters.bathrooms = Array.isArray(this.filters.bathrooms)
                    ? [...this.filters.bathrooms]
                    : this.filters.bathrooms;
            }

            // Booleanos (solo si están activos)
            if (this.filters.servicio_pro) filters.servicio_pro = true;
            if (this.filters.multifamily) filters.multifamily = true;
            if (this.filters.has_discount) filters.has_discount = true;
            if (this.filters.free_commission) filters.free_commission = true;
            if (this.filters.fixed_price) filters.fixed_price = true;
            if (this.filters.furnished) filters.furnished = true;
            if (this.filters.flex) filters.flex = true;
            if (this.filters.accepts_pets) filters.accepts_pets = true;
            if (this.filters.has_parking) filters.has_parking = true;
            if (this.filters.has_storage) filters.has_storage = true;
            if (this.filters.has_terrace) filters.has_terrace = true;
            if (this.filters.has_washer_connection) filters.has_washer_connection = true;
            if (this.filters.has_laundry) filters.has_laundry = true;
            if (this.filters.super_promo) filters.super_promo = true;
            if (this.filters.close_to_metro) filters.close_to_metro = true;
            if (this.filters.no_require_aval) filters.no_require_aval = true;
            if (this.filters.no_rent_warranty) filters.no_rent_warranty = true;
            if (this.filters.warranty_pro) filters.warranty_pro = true;

            // Incluir commune_id si está activo (priorizar sobre commune_slug)
            if (this.communeId) {
                filters.commune_id = this.communeId;
            } else if (this.communeSlug) {
                // Mantener compatibilidad con commune_slug si communeId no está disponible
                filters.commune_slug = this.communeSlug;
            }

            return filters;
        },
        /**
         * Actualizar marcadores en el mapa
         */
        async updateMapMarkers(buildingsSource = null) {
            if (!this.mapbox?.map) {
                this.warn('❌ Mapbox no está inicializado');
                return;
            }

            // Esperar a que el mapa esté completamente cargado con timeout
            try {
                await Promise.race([
                    this.mapbox.waitForMapLoad(),
                    new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Timeout esperando carga del mapa')), 3000)
                    )
                ]);
            } catch (error) {
                this.warn('⚠️ Error esperando carga del mapa:', error.message);
                // Continuar de todas formas si el mapa ya está cargado
                if (!this.mapbox.map.loaded()) {
                    this.warn('⚠️ El mapa no está cargado pero continuaremos de todas formas');
                    // No lanzar el error, intentar actualizar de todas formas
                }
            }

            // Usar la fuente proporcionada o el estado actual
            const source = buildingsSource || this.buildings;

            if (!source || source.length === 0) {
                this.log('⚠️ No hay edificios para mostrar en el mapa');
                this.mapbox.dataToMap({ type: 'FeatureCollection', features: [] });
                return;
            }

            this.log(`🔨 Actualizando mapa con ${source.length} edificios`);

            // Construcción eficiente de GeoJSON usando array crudo si es posible
            const geojson = this.buildGeoJSON(source);

            try {
                await this.mapbox.dataToMap(geojson);
                this.log('✅ Marcadores actualizados');
                this.verifyMapSource();
            } catch (error) {
                console.error('❌ Error al actualizar marcadores:', error);
            }
        },

        /**
         * Construir GeoJSON desde los edificios
         * Agrupa documentos retail por building_id para evitar marcadores duplicados
         */
        buildGeoJSON(buildingsSource = null) {
            // Agrupar edificios por building_id para consolidar retail
            const buildingsMap = new Map();
            const source = buildingsSource || this.buildings;

            source.forEach(building => {
                if (!building._geo || !building._geo.lat || !building._geo.lng) return;

                const buildingId = building.building_id || building.id;

                if (!buildingsMap.has(buildingId)) {
                    // Primera vez que vemos este edificio - Pick solo lo necesario
                    buildingsMap.set(buildingId, {
                        building_id: buildingId,
                        nombre: building.nombre,
                        direccion: building.direccion,
                        comuna: building.comuna,
                        min_price: building.min_price || building.price,
                        max_price: building.max_price || building.price,
                        servicio_pro: !!building.servicio_pro,
                        multifamily: !!building.multifamily,
                        super_promo: !!building.super_promo,
                        has_discount: !!building.has_discount,
                        accepts_pets: !!building.accepts_pets,
                        _geo: building._geo,
                        total_available_count: building.multifamily ? (building.total_available_count || 1) : 1
                    });
                } else if (!building.multifamily) {
                    // Es retail y ya existe este building_id, agregar esta unidad
                    const existing = buildingsMap.get(buildingId);
                    existing.total_available_count += 1;

                    const price = building.price || 0;
                    if (price < existing.min_price) existing.min_price = price;
                    if (price > existing.max_price) existing.max_price = price;

                    existing.has_discount = existing.has_discount || !!building.has_discount;
                    existing.accepts_pets = existing.accepts_pets || !!building.accepts_pets;
                    existing.super_promo = existing.super_promo || !!building.super_promo;
                }
            });

            // Convertir el Map a features de GeoJSON
            const features = Array.from(buildingsMap.values()).map(building => ({
                id: building.id, // Top-level ID for feature-state
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [building._geo.lng, building._geo.lat]
                },
                properties: {
                    building_id: building.building_id,
                    nombre: building.nombre || 'Sin nombre',
                    direccion: building.direccion || 'Sin dirección',
                    comuna: building.comuna || 'Sin comuna',
                    min_price: building.min_price || 0,
                    max_price: building.max_price || 0,
                    units: building.total_available_count || 0,
                    servicio_pro: building.servicio_pro || false,
                    multifamily: building.multifamily || false,
                    super_promo: building.super_promo || false,
                    has_discount: building.has_discount || false,
                    accepts_pets: building.accepts_pets || false,
                }
            }));

            this.log(`📍 GeoJSON creado: ${features.length} marcadores únicos de ${this.buildings.length} documentos`);

            return {
                type: 'FeatureCollection',
                features: features
            };
        },

        /**
         * Limpiar marcadores del mapa
         */
        clearMapMarkers() {
            const emptyGeojson = {
                type: 'FeatureCollection',
                features: []
            };

            try {
                this.mapbox.dataToMap(emptyGeojson);
                this.log('🧹 Mapa limpiado');
            } catch (error) {
                console.error('❌ Error al limpiar mapa:', error);
            }
        },

        /**
         * Verificar que el source del mapa se actualizó correctamente
         */
        verifyMapSource() {
            if (!this.config.debug) return;

            setTimeout(() => {
                const source = this.mapbox.map.getSource('buildings');
                if (source) {
                    const data = source._data;
                    this.log('✅ Source "buildings" verificado:', {
                        features: data?.features?.length || 0
                    });
                } else {
                    this.warn('❌ Source "buildings" no existe');
                }
            }, 100);
        },
        /**
         * Dibujar el bounding box en el mapa para debugging
         */
        drawBoundingBox(bounds) {
            const map = this.mapbox.map;

            // Remover bbox anterior si existe
            if (map.getLayer('debug-bbox-line')) {
                map.removeLayer('debug-bbox-line');
            }
            if (map.getLayer('debug-bbox-fill')) {
                map.removeLayer('debug-bbox-fill');
            }
            if (map.getSource('debug-bbox')) {
                map.removeSource('debug-bbox');
            }

            // Crear polígono del bbox
            const bboxPolygon = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [[
                        [bounds.west, bounds.north],
                        [bounds.east, bounds.north],
                        [bounds.east, bounds.south],
                        [bounds.west, bounds.south],
                        [bounds.west, bounds.north]
                    ]]
                }
            };

            // Agregar source y layers
            map.addSource('debug-bbox', {
                type: 'geojson',
                data: bboxPolygon
            });

            map.addLayer({
                id: 'debug-bbox-fill',
                type: 'fill',
                source: 'debug-bbox',
                paint: {
                    'fill-color': '#ff0000',
                    'fill-opacity': 0.1
                }
            });

            map.addLayer({
                id: 'debug-bbox-line',
                type: 'line',
                source: 'debug-bbox',
                paint: {
                    'line-color': '#ff0000',
                    'line-width': 2,
                    'line-dasharray': [2, 2]
                }
            });

            this.log('🔴 BBox dibujado:', bounds);
        },
        /**
         * Manejar click en cluster
         */
        handleClusterClick(buildingIds) {
            const clusterBuildings = this.buildings.filter(b =>
                buildingIds.some(id => String(id) === String(b.building_id))
            );

            this.log(`👥 Cluster con ${clusterBuildings.length} edificios`, clusterBuildings);

            this.$dispatch('cluster-selected', {
                buildings: clusterBuildings,
                buildingIds: buildingIds,
                count: clusterBuildings.length
            });
        },

        /**
         * Manejar click en edificio individual
         */
        async handleBuildingClick(buildingId) {
            // Buscar TODOS los documentos con este building_id (para retail con múltiples unidades)
            let buildings = this.buildings.filter(b => String(b.building_id) === String(buildingId));

            // Si no hay documentos locales, intentar obtenerlo de la API
            if (buildings.length === 0) {
                this.log(`🔍 Cargando edificio ${buildingId} desde API`);
                const building = await this.fetchBuildingById(buildingId);
                if (building) {
                    buildings = [building];
                }
            }

            if (buildings.length > 0) {
                // Si es multifamily o solo hay una unidad, usar el primero
                const mainBuilding = buildings[0];
                this.selectedBuilding = mainBuilding;
                this.highlightedBuildingId = buildingId;

                this.log('🏢 Edificio seleccionado:', mainBuilding, `(${buildings.length} documento(s))`);

                // Si hay múltiples documentos (retail), tratarlo como cluster
                if (buildings.length > 1) {
                    this.$dispatch('cluster-selected', {
                        buildings: buildings,
                        buildingIds: [buildingId],
                        count: buildings.length
                    });
                } else {
                    this.$dispatch('building-selected', {
                        building: mainBuilding,
                        buildingId: buildingId
                    });
                }
            } else {
                this.warn(`⚠️ No se pudo cargar edificio ${buildingId}`);
            }
        },

        /**
         * Obtener edificio por ID desde la API
         */
        async fetchBuildingById(buildingId) {
            try {
                const response = await fetch(`/api/buildings/${buildingId}`, {
                    headers: {
                        'Accept': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.success) {
                    return data.data;
                }

                return null;
            } catch (error) {
                console.error(`Error al obtener edificio ${buildingId}:`, error);
                return null;
            }
        },

        /**
         * Ir a un edificio específico (centrar y hacer zoom)
         */
        goToBuilding(buildingId, zoom = 15) {
            const building = this.buildings.find(b => b.building_id === buildingId);

            if (!building || !building._geo) {
                this.warn(`⚠️ No se puede ir al edificio ${buildingId}`);
                return;
            }

            this.log(`📍 Navegando a edificio ${buildingId}`);

            const targetCenter = { lat: building._geo.lat, lng: building._geo.lng };
            this.updateUrl(false, false, targetCenter, zoom);

            this.mapbox.map.flyTo({
                center: [building._geo.lng, building._geo.lat],
                zoom: zoom,
                duration: 3000,
                essential: true
            }, { programmatic: true });
        },

        /**
         * Ajustar vista para mostrar todos los edificios
         */
        fitBounds() {
            if (!this.buildings || this.buildings.length === 0) {
                this.warn('⚠️ No hay edificios para ajustar vista');
                return;
            }

            const coordinates = this.buildings
            .filter(b => b._geo && b._geo.lat && b._geo.lng)
            .map(b => {
                let lat = parseFloat(b._geo.lat);
                let lng = parseFloat(b._geo.lng);
                // Healer on building data?
                // If the entire dataset is shifted to Los Angeles, this healer won't help if they are valid values.
                return [lng, lat];
            });

            if (coordinates.length === 0) return;

            // Calcular bounds manualmente
            const lngs = coordinates.map(coord => coord[0]);
            const lats = coordinates.map(coord => coord[1]);

            const bounds = [
                [Math.min(...lngs), Math.min(...lats)], // southwest
                [Math.max(...lngs), Math.max(...lats)]  // northeast
            ];

            if (this.config.debug) {
                this.log('📏 Calculando bounds para edificios:', { 
                    sw: bounds[0], 
                    ne: bounds[1],
                    count: coordinates.length 
                });
            }

            this.mapbox.map.fitBounds(bounds, {
                padding: 50,
                maxZoom: 15,
                duration: 1000
            }, { programmatic: true });

            this.log('🗺️ Vista ajustada a edificios');
        },
        /**
         * Toggle filtro de dormitorios
         * SIEMPRE permite solo una selección (igual que autocomplete)
         */
        toggleBedroom(bedroom) {
            const index = this.filters.bedrooms.indexOf(bedroom);

            if (index > -1) {
                // Si ya estaba seleccionado, deseleccionarlo
                this.filters.bedrooms = [];
            } else {
                // Reemplazar toda la selección con el nuevo valor (SIEMPRE selección única)
                this.filters.bedrooms = [bedroom];
            }

            this.log(`🛏️ Filtro dormitorios:`, [...this.filters.bedrooms], 'Exacto:', this.filters.bedrooms_exact_match);
        },

        /**
         * Toggle filtro de dormitorios (Temporal)
         */
        toggleTempBedroom(bedroom) {
            const index = this.tempFilters.bedrooms.indexOf(bedroom);

            if (index > -1) {
                this.tempFilters.bedrooms = [];
            } else {
                this.tempFilters.bedrooms = [bedroom];
            }
        },

        /**
         * Toggle filtro de baños
         * SIEMPRE permite solo una selección (igual que dormitorios)
         */
        toggleBathroom(bathroom) {
            const index = this.filters.bathrooms.indexOf(bathroom);

            if (index > -1) {
                // Si ya estaba seleccionado, deseleccionarlo
                this.filters.bathrooms = [];
            } else {
                // Reemplazar toda la selección con el nuevo valor (SIEMPRE selección única)
                this.filters.bathrooms = [bathroom];
            }

            this.log(`🚿 Filtro baños:`, [...this.filters.bathrooms]);
        },

        /**
         * Toggle filtro de baños (Temporal)
         */
        toggleTempBathroom(bathroom) {
            const index = this.tempFilters.bathrooms.indexOf(bathroom);

            if (index > -1) {
                this.tempFilters.bathrooms = [];
            } else {
                this.tempFilters.bathrooms = [bathroom];
            }
        },

        /**
         * Limpiar todos los filtros
         */
        clearFilters() {
            this.log('🧹 Limpiando filtros');
            let reason = 'apply_filters';

            //Si no se aplicaron filtros y no hay resultados, cambiar la razon a initial_load

            if (this.getActiveFiltersCount() === 0 && this.communeId){
                reason = 'initial_load';
            }

            // Resetear communeSlug para que updateUrl() lo elimine del pathname/params
            this.communeSlug = null;
            this.isCommuneSearch = false;
            this.communeName = '';
            this.communeId = null;

            this.sortBy = 'relevance';

            this.filters = {
                min_price: null,
                max_price: null,
                bedrooms: [],
                bedrooms_exact_match: false,
                bathrooms: [],
                servicio_pro: false,
                multifamily: false,
                has_discount: false,
                free_commission: false,
                fixed_price: false,
                furnished: false,
                flex: false,
                accepts_pets: false,
                has_parking: false,
                has_storage: false,
                has_terrace: false,
                has_washer_connection: false,
                has_laundry: false,
                super_promo: false,
                close_to_metro: false,
                commune_slug: null,
                warranty_pro: false,
                no_require_aval: false,
                no_rent_warranty: false
            };

            // Resetear mapa a coordenadas default
            if (this.mapbox?.map) {
                this.lastProgrammaticMapMove = Date.now();
                this.mapbox.map.jumpTo({
                    center: [this.defaultCenter.lng, this.defaultCenter.lat],
                    zoom: this.defaultZoom,
                });
            }

            // Sincronizar filtros temporales también para que el drawer quede limpio
            this.syncFiltersToTemp();

            this.updateUrl();
            this.searchBuildings(reason);
        },

        /**
         * Sincronizar filtros principales a temporales
         */
        syncFiltersToTemp() {
            this.tempFilters = JSON.parse(JSON.stringify(this.filters));
        },

        /**
         * Aplicar filtros temporales a principales y buscar
         */
        applyTempFilters() {
            this.filters = JSON.parse(JSON.stringify(this.tempFilters));
            this.applyFilters();

            if (window.assetplanAnalytics) {
                window.assetplanAnalytics.for('sitio_web').track('filters_apply');
            }
        },

        /**
         * Aplicar filtros (búsqueda inmediata)
         */
        applyFilters() {
            this.log('✅ Aplicando filtros');
            this.updateUrl();
            this.searchBuildings('clear_filters');
        },

        /**
         * Obtener conteo de filtros activos
         */
        getActiveFiltersCount() {
            let count = 0;

            if (this.filters.min_price) count++;
            if (this.filters.max_price) count++;
            if (this.filters.bedrooms.length > 0) count++;
            if (this.filters.bathrooms.length > 0) count++;
            if (this.filters.servicio_pro) count++;
            if (this.filters.multifamily) count++;
            if (this.filters.has_discount) count++;
            if (this.filters.free_commission) count++;
            if (this.filters.fixed_price) count++;
            if (this.filters.furnished) count++;
            if (this.filters.flex) count++;
            if (this.filters.accepts_pets) count++;
            if (this.filters.has_parking) count++;
            if (this.filters.has_storage) count++;
            if (this.filters.has_terrace) count++;
            if (this.filters.has_washer_connection) count++;
            if (this.filters.has_laundry) count++;
            if (this.filters.super_promo) count++;
            if (this.filters.close_to_metro) count++;
            if (this.filters.warranty_pro) count++;
            if (this.filters.no_require_aval) count++;
            if (this.filters.no_rent_warranty) count++;

            return count;
        },
        /**
         * Formatear precio en pesos chilenos
         */
        formatPrice(price) {
            if (!price || price === 0) return 'Sin precio';

            return new Intl.NumberFormat('es-CL', {
                style: 'currency',
                currency: 'CLP',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(price);
        },

        /**
         * Formatear número con separadores de miles
         */
        formatNumber(number) {
            return new Intl.NumberFormat('es-CL').format(number);
        },

        /**
         * Solo permite números
         */
        onlyNumbers(event) {
            if (!/\d/.test(event.key)) {
                event.preventDefault();
            }
        },

        /**
         * Gestiona la entrada de datos para filtros de precio.
        */
        handlePriceInput(event, key) {
            const input = event.target;
            // Quitamos los puntos y cualquier cosa no numérica
            const cleanValue = input.value.replaceAll(/\D/g, '');

            if (cleanValue === '') {
                this.tempFilters[key] = null;
                input.value = '';
                return;
            }

            // Guardamos el número real (limpio) en el objeto temporal
            const numericValue = Number.parseInt(cleanValue);
            
            this.tempFilters[key] = numericValue;

            // Actualizamos el valor visual del input usando tu función existente
            input.value = this.formatNumber(numericValue);
        },

        /**
         * Logging condicional
         */
        log(...args) {
            if (this.config.debug) {
                console.log('[BuildingMapSearch]', ...args);
            }
        },

        /**
         * Warning condicional
         */
        warn(...args) {
            if (this.config.debug) {
                console.warn('[BuildingMapSearch]', ...args);
            }
        },

        /**
         * Obtener resumen de estado
         */
        getStateSummary() {
            return {
                isReady: this.isMapReady,
                buildingsCount: this.buildings.length,
                totalBuildings: this.totalBuildings,
                isLoading: this.loading,
                hasError: !!this.error,
                activeFilters: this.getActiveFiltersCount(),
                stats: { ...this.stats }
            };
        },

        /**
         * Refrescar datos (forzar nueva búsqueda)
         */
        refresh() {
            this.log('🔄 Refrescando datos');
            this.searchBuildings('marker_selection');
        },

        /**
         * Actualizar configuración dinámica
         */
        updateConfig(newConfig) {
            this.config = {
                ...this.config,
                ...newConfig
            };

            this.log('⚙️ Configuración actualizada:', this.config);

            // Si se cambió el clustering, actualizar
            if ('clusterRadius' in newConfig || 'clusterMaxZoom' in newConfig) {
                this.mapbox.updateClusterOptions({
                    clusterRadius: this.config.clusterRadius,
                    clusterMaxZoom: this.config.clusterMaxZoom
                });
            }
        },

        /**
         * Manejar evento de edificio seleccionado (para uso en x-data)
         * Llamado desde: @building-selected.window="handleBuildingSelected($event)"
         */
        handleBuildingSelectedEvent(event) {
            const { building, buildingId } = event.detail;
            this.log('🏢 Evento building-selected recibido:', buildingId);

            // Ya manejado internamente por handleBuildingClick
            // Este método está disponible para lógica adicional si se necesita
        },

        /**
         * Manejar evento de cluster seleccionado (para uso en x-data)
         * Llamado desde: @cluster-selected.window="handleClusterSelected($event)"
         */
        handleClusterSelectedEvent(event) {
            const { buildings, buildingIds, count } = event.detail;
            this.log('👥 Evento cluster-selected recibido:', count, 'edificios');

            // Ya manejado internamente por handleClusterClick
            // Este método está disponible para lógica adicional si se necesita
        },

        /**
         * Ordenar los edificios según el criterio seleccionado
         */
        /**
         * Ordenar lista de edificios (Helper puro)
         */
        _sortBuildingsList(buildingsList) {
            const list = [...buildingsList];
            const sortType = this.sortBy || 'relevance';

            switch (sortType) {
                case 'price-asc':
                    list.sort((a, b) => (a.min_price || 0) - (b.min_price || 0));
                    break;
                case 'price-desc':
                    list.sort((a, b) => (b.min_price || 0) - (a.min_price || 0));
                    break;
                case 'units-desc':
                    list.sort((a, b) => (b.total_available_count || 0) - (a.total_available_count || 0));
                    break;
                case 'relevance':
                default:
                     list.sort((a, b) => {
                        // 1. has_available_units (descendente - true primero)
                        const hasUnitsA = a.has_available_units ? 1 : 0;
                        const hasUnitsB = b.has_available_units ? 1 : 0;
                        if (hasUnitsA !== hasUnitsB) return hasUnitsB - hasUnitsA;

                        // 2. servicio_pro (descendente - true primero)
                        if (!!a.servicio_pro !== !!b.servicio_pro) return (b.servicio_pro ? 1 : 0) - (a.servicio_pro ? 1 : 0);

                        // 3. multifamily (descendente - true primero)
                        if (!!a.multifamily !== !!b.multifamily) return (b.multifamily ? 1 : 0) - (a.multifamily ? 1 : 0);

                        // 4. total_available_count (descendente)
                        return (b.total_available_count || 0) - (a.total_available_count || 0);
                    });
                    break;
            }
            return list;
        },

        /**
         * Ordenar los edificios actuales y actualizar estado
         */
        sortBuildings() {
            if (!this.buildings || this.buildings.length === 0) return;

            const startTime = performance.now();

            safeStartTransition(() => {
                this.buildings = this._sortBuildingsList(this.buildings);
            });

            const endTime = performance.now();
            this.log(`✅ Edificios ordenados por ${this.sortBy} en ${(endTime - startTime).toFixed(2)}ms`);

            this.updateUrl();
        },

        /**
         * Obtener label del ordenamiento actual
         */
        getCurrentSortLabel() {
            const option = this.sortOptions.find(opt => opt.value === this.sortBy);
            return option ? option.label : 'Relevancia';
        },

        /**
         * Leer filtros y ordenamiento desde la URL
         */
        loadFromUrl() {
            const params = new URLSearchParams(window.location.search);

            // Cargar coordenadas del mapa
            const lat = params.get('lat');
            const lng = params.get('lng');
            const zoom = params.get('zoom');

            let mapConfig = null;
            let hasCoordinates = false;

            if (lat && lng) {
                let latitude = parseFloat(lat);
                let longitude = parseFloat(lng);
                const zoomLevel = zoom ? parseFloat(zoom) : initialZoom;

                if (!isNaN(latitude) && !isNaN(longitude)) {
                    // Healer: Detectar coordenadas invertidas típicas de Chile
                    if (latitude < -60 && longitude > -60) {
                        this.warn('🔄 Coordenadas cargadas de URL parecen invertidas, corrigiendo...');
                        const temp = latitude;
                        latitude = longitude;
                        longitude = temp;
                    }

                    const center = [longitude, latitude];
                    const finalZoom = !isNaN(zoomLevel) ? zoomLevel : initialZoom;

                    this.log('📍 Coordenadas cargadas desde URL', {
                        lat: latitude,
                        lng: longitude,
                        zoom: finalZoom
                    });

                    // Guardar configuración para devolverla
                    mapConfig = { center, zoom: finalZoom };
                    hasCoordinates = true;
                }
            }

            // Cargar ordenamiento
            const sort = params.get('sort');
            if (sort && this.sortOptions.find(opt => opt.value === sort)) {
                this.sortBy = sort;
            }

            // Cargar filtros
            const minPrice = params.get('min_price');
            if (minPrice) this.filters.min_price = parseInt(minPrice);

            const maxPrice = params.get('max_price');
            if (maxPrice) this.filters.max_price = parseInt(maxPrice);

            const bedrooms = params.get('bedrooms');
            if (bedrooms) {
                this.filters.bedrooms = bedrooms.split(',').map(b => parseInt(b));
            }

            const bedroomsExactMatch = params.get('bedrooms_exact_match') || params.get('exact');
            if (bedroomsExactMatch) {
                this.filters.bedrooms_exact_match = bedroomsExactMatch === 'true' || bedroomsExactMatch === '1';
            }

            const bathrooms = params.get('bathrooms');
            if (bathrooms) {
                this.filters.bathrooms = bathrooms.split(',').map(b => parseInt(b));
            }

            const servicioProParam = params.get('servicio_pro');
            if (servicioProParam) {
                this.filters.servicio_pro = servicioProParam === 'true' || servicioProParam === '1';
            }

            const multifamilyParam = params.get('multifamily');
            if (multifamilyParam) {
                this.filters.multifamily = multifamilyParam === 'true' || multifamilyParam === '1';
            }

            const hasDiscountParam = params.get('has_discount');
            if (hasDiscountParam) {
                this.filters.has_discount = hasDiscountParam === 'true' || hasDiscountParam === '1';
            }

            const acceptsPetsParam = params.get('accepts_pets');
            if (acceptsPetsParam) {
                this.filters.accepts_pets = acceptsPetsParam === 'true' || acceptsPetsParam === '1';
            }

            const hasParkingParam = params.get('has_parking');
            if (hasParkingParam) {
                this.filters.has_parking = hasParkingParam === 'true' || hasParkingParam === '1';
            }

            const hasStorageParam = params.get('has_storage');
            if (hasStorageParam) {
                this.filters.has_storage = hasStorageParam === 'true' || hasStorageParam === '1';
            }

            const hasTerraceParam = params.get('has_terrace');
            if (hasTerraceParam) {
                this.filters.has_terrace = hasTerraceParam === 'true' || hasTerraceParam === '1';
            }

            const hasWasherConnectionParam = params.get('has_washer_connection');
            if (hasWasherConnectionParam) {
                this.filters.has_washer_connection = hasWasherConnectionParam === 'true' || hasWasherConnectionParam === '1';
            }

            const hasLaundryParam = params.get('has_laundry');
            if (hasLaundryParam) {
                this.filters.has_laundry = hasLaundryParam === 'true' || hasLaundryParam === '1';
            }

            const superPromoParam = params.get('super_promo');
            if (superPromoParam) {
                this.filters.super_promo = superPromoParam === 'true' || superPromoParam === '1';
            }

            const closeToMetroParam = params.get('close_to_metro');
            if (closeToMetroParam) {
                this.filters.close_to_metro = closeToMetroParam === 'true' || closeToMetroParam === '1';
            }

            const freeCommissionParam = params.get('free_commission');
            if (freeCommissionParam) {
                this.filters.free_commission = freeCommissionParam === 'true' || freeCommissionParam === '1';
            }

            const fixedPriceParam = params.get('fixed_price');
            if (fixedPriceParam) {
                this.filters.fixed_price = fixedPriceParam === 'true' || fixedPriceParam === '1';
            }

            const furnishedParam = params.get('furnished');
            if (furnishedParam) {
                this.filters.furnished = furnishedParam === 'true' || furnishedParam === '1';
            }

            const noAvalParam = params.get('flex');
            if (noAvalParam) {
                this.filters.flex = noAvalParam === 'true' || noAvalParam === '1';
            }

            const onlyNoAvalParam = params.get('no_require_aval');
            if (onlyNoAvalParam) {
                this.filters.no_require_aval = onlyNoAvalParam === 'true' || onlyNoAvalParam === '1';
            }

            const onlyNoWarrantyParam = params.get('no_rent_warranty');
            if (onlyNoWarrantyParam) {
                this.filters.no_rent_warranty = onlyNoWarrantyParam === 'true' || onlyNoWarrantyParam === '1';
            }

            const warrantyProParam = params.get('warranty_pro');
            if (warrantyProParam) {
                this.filters.warranty_pro = warrantyProParam === 'true' || warrantyProParam === '1';
            }


            this.log('📥 Filtros y ordenamiento cargados desde URL', {
                sortBy: this.sortBy,
                bedroomsCount: this.filters.bedrooms?.length || 0,
                bathroomsCount: this.filters.bathrooms?.length || 0,
                hasCoordinates
            });

            // Si hay commune_id o commune_slug pero no coordenadas, buscar por comuna
            if ((this.communeId || this.communeSlug) && !hasCoordinates) {
                this.log('🏘️ Se buscará por comuna al inicializar el mapa', {
                    communeId: this.communeId,
                    communeSlug: this.communeSlug
                });
            }
            
            return mapConfig;
        },

        /**
         * Actualizar la URL con los filtros y ordenamiento actuales
         */
        updateUrl(isMapEvent = false, eventIsProgrammatic = false, forceCenter = null, forceZoom = null) {
            if (!this.mapbox || !this.mapbox.map) return;

            const now = Date.now();
            const PROGRAMMATIC_WINDOW = 3500;
            const isInsideProgrammaticWindow = this.lastProgrammaticMapMove && (now - this.lastProgrammaticMapMove) < PROGRAMMATIC_WINDOW;

            // Bloqueamos actualizaciones automáticas de URL originadas por el mapa si son programadas
            // o si estamos dentro de una "ventana de gracia" tras una acción programada.
            if (isMapEvent && (eventIsProgrammatic || isInsideProgrammaticWindow) && !forceCenter) {
                return;
            }

            const center = forceCenter || this.mapbox.map.getCenter();
            const zoom = forceZoom || this.mapbox.map.getZoom();

            if (!center) return;

            const params = new URLSearchParams(window.location.search);
            
            // Extraer lat/lng
            let latValue, lngValue;
            if (typeof center.lat === 'function') {
                latValue = center.lat();
                lngValue = center.lng();
            } else if (center.lat !== undefined) {
                latValue = center.lat;
                lngValue = center.lng;
            } else if (Array.isArray(center)) {
                lngValue = center[0];
                latValue = center[1];
            }

            // Healer: Chile coordinates
            if (latValue < -60 && lngValue > -60) {
                const temp = latValue;
                latValue = lngValue;
                lngValue = temp;
            }

            // Un movimiento es MANUAL si es un evento de mapa, no viene marcado como auto,
            // y estamos FUERA de la ventana de protección programática.
            const isActuallyManualMove = isMapEvent && !eventIsProgrammatic && !isInsideProgrammaticWindow;

            // Si es un movimiento manual real y estábamos en modo comuna, liberamos el filtro.
            if (isActuallyManualMove && (this.communeId || this.communeSlug || this.isCommuneSearch)) {
                this.log('🔓 Movimiento manual confirmado: Liberando búsqueda de comuna');
                this.communeSlug = null;
                this.communeId = null;
                this.isCommuneSearch = false;
                this.communeName = ''; // Limpiar nombre visual
                window.dispatchEvent(new CustomEvent('commune-cleared'));
            }

            // Determinamos si mostramos parámetros de mapa (lat, lng, zoom).
            // Se muestran si: MODO MAPA, o MOVIMIENTO MANUAL, o NO HAY COMUNA.
            const shouldShowMapParams = (this.viewMode === 'map') || isActuallyManualMove || !this.communeSlug;

            if (shouldShowMapParams) {
                params.set('lat', parseFloat(latValue).toFixed(6));
                params.set('lng', parseFloat(lngValue).toFixed(6));
                params.set('zoom', parseFloat(zoom).toFixed(2));
            } else {
                params.delete('lat');
                params.delete('lng');
                params.delete('zoom');
            }

            // Sincronizar viewMode en URL
            if (this.viewMode === 'map') {
                params.set('mode', 'map');
            } else {
                params.delete('mode');
            }

            params.delete('q');
            params.delete('exact');
            params.delete('bedrooms_exact');
            params.delete('commune');

            // Sincronizar filtros
            Object.keys(this.filters).forEach(key => {
                if (key === 'commune_slug') return;
                const value = this.filters[key];
                if (value !== null && value !== undefined && value !== '') {
                    if (Array.isArray(value)) {
                        if (value.length > 0) params.set(key, value.join(','));
                        else params.delete(key);
                    } else if (typeof value === 'boolean') {
                        if (value === true) params.set(key, '1');
                        else params.delete(key);
                    } else {
                        params.set(key, value);
                    }
                } else {
                    params.delete(key);
                }
            });

            if (this.sortBy && this.sortBy !== 'relevance') {
                params.set('sort', this.sortBy);
            } else {
                params.delete('sort');
            }

            let pathname = window.location.pathname;

            // Sincronizar pathname según el estado del slug de comuna (mantener compatibilidad con URLs)
            // Note: Las URLs siguen usando slug por SEO, pero internamente usamos communeId
            if (this.communeSlug) {
                if (!pathname.includes('/arriendo/departamento/' + this.communeSlug)) {
                    pathname = '/arriendo/departamento/' + this.communeSlug;
                }
            } else if (pathname.includes('/arriendo/departamento/')) {
                pathname = '/arriendo/departamento';
            }

            const newUrl = params.toString() ? `${pathname}?${params.toString()}` : pathname;

            if (window.location.href !== (window.location.origin + newUrl)) {
                window.history.replaceState({}, '', newUrl);
                this.log('📤 URL sincronizada', { url: newUrl, isActuallyManualMove, isMapEvent });
            }
        }
    }
}
